// src/utils/roundSchedule.js
// Shared pure helper for per-round start times on Create Event and Admin Branded Events.

export const GAP_STEPS = [
  { min: 5, label: '5m' },
  { min: 10, label: '10m' },
  { min: 15, label: '15m' },
  { min: 30, label: '30m' },
  { min: 60, label: '1h' },
  { min: 1440, label: '1d' },
  { min: 10080, label: '1w' },
  { min: 43200, label: '1mo' },
];

export const DEFAULT_GAP_MINUTES = 5;
export const DAY_ANCHOR_MINUTES = 1440;

export function toEpochMs(val) {
  if (val == null || val === '') return null;
  if (typeof val === 'number') {
    return Number.isFinite(val) && val > 0 ? Math.round(val) : null;
  }
  if (val instanceof Date) {
    const ms = val.getTime();
    return Number.isFinite(ms) && ms > 0 ? Math.round(ms) : null;
  }
  if (typeof val?.toMillis === 'function') {
    const ms = val.toMillis();
    return Number.isFinite(ms) && ms > 0 ? Math.round(ms) : null;
  }
  if (typeof val === 'object') {
    const sec = Number(val._seconds ?? val.seconds);
    if (Number.isFinite(sec) && sec > 0) {
      return Math.round(sec * 1000);
    }
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) {
      const n = Number(trimmed);
      return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    }
    const localMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})$/);
    if (localMatch) {
      const [, y, m, d, hh, mm] = localMatch.map(Number);
      const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
      const ms = dt.getTime();
      return Number.isFinite(ms) && ms > 0 ? Math.round(ms) : null;
    }
    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
  }
  return null;
}

export function formatRoundLocalTime(ms) {
  const epoch = toEpochMs(ms);
  if (!Number.isFinite(epoch)) return 'Not set';
  const d = new Date(epoch);
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value || '';
    const weekday = get('weekday');
    const month = get('month');
    const day = get('day');
    const hour = get('hour');
    const minute = get('minute');
    const dayPeriod = get('dayPeriod');
    const tz = get('timeZoneName');
    return `${weekday} ${month} ${day}, ${hour}:${minute}${dayPeriod ? ` ${dayPeriod}` : ''}${tz ? ` ${tz}` : ''}`.trim();
  } catch (_) {
    return d.toLocaleString();
  }
}

export function formatGapLabel(minutes) {
  const m = Number(minutes) || DEFAULT_GAP_MINUTES;
  if (m >= DAY_ANCHOR_MINUTES && m % DAY_ANCHOR_MINUTES === 0) {
    const days = m / DAY_ANCHOR_MINUTES;
    return days === 1 ? '1 day' : `${days} days`;
  }
  if (m >= 60 && m % 60 === 0) {
    return `${m / 60}h`;
  }
  return `${m}m`;
}

export function rowsFromGap({
  totalRounds,
  startAtMs,
  gapMinutes = DEFAULT_GAP_MINUTES,
  perRoundMinutes = 0,
} = {}) {
  const rounds = Math.floor(Number(totalRounds)) || 0;
  if (rounds < 2) return [];
  const baseMs = toEpochMs(startAtMs);
  const gap = Number(gapMinutes) > 0 ? Number(gapMinutes) : DEFAULT_GAP_MINUTES;
  const playMin = Math.max(0, Number(perRoundMinutes) || 0);
  const stepMin = gap >= DAY_ANCHOR_MINUTES ? gap : gap + playMin;

  const rows = [];
  for (let r = 2; r <= rounds; r++) {
    const startMs = Number.isFinite(baseMs)
      ? Math.round(baseMs + (r - 1) * stepMin * 60000)
      : null;
    rows.push({
      round: r,
      label: `Round ${r} starts`,
      startMs,
      formatted: formatRoundLocalTime(startMs),
    });
  }
  return rows;
}

export function roundTimesFromGap(opts = {}) {
  const rows = rowsFromGap(opts);
  const out = {};
  for (const row of rows) {
    out[row.round] = row.startMs;
  }
  return out;
}

export function normalizeRoundStartTimes(raw, totalRounds) {
  if (!raw || typeof raw !== 'object') return {};
  const rounds = Math.floor(Number(totalRounds)) || 0;
  const out = {};
  const maxR = rounds >= 2 ? rounds : 64;
  for (let r = 2; r <= maxR; r++) {
    if (Object.prototype.hasOwnProperty.call(raw, r) || Object.prototype.hasOwnProperty.call(raw, String(r))) {
      const v = raw[r] ?? raw[String(r)];
      out[r] = toEpochMs(v);
    }
  }
  return out;
}

export function validateRoundScheduleClient({
  mode = 'gap',
  totalRounds = 1,
  startAtMs = null,
  roundStartTimes = null,
} = {}) {
  const rounds = Math.floor(Number(totalRounds)) || 0;
  if (rounds < 2 || (mode || 'gap') !== 'scheduled') {
    return { ok: true, code: null, round: null, error: null, roundStartTimes: null };
  }

  const r1Ms = toEpochMs(startAtMs);
  if (!Number.isFinite(r1Ms)) {
    return {
      ok: false,
      code: 'round-schedule-needs-start',
      round: 1,
      error: 'Pick a start time for the event first.',
      roundStartTimes: null,
    };
  }

  const raw = roundStartTimes && typeof roundStartTimes === 'object' ? roundStartTimes : {};
  const out = {};
  let prevMs = r1Ms;
  for (let r = 2; r <= rounds; r++) {
    const v = raw[r] ?? raw[String(r)];
    const ms = toEpochMs(v);
    if (!Number.isFinite(ms)) {
      return {
        ok: false,
        code: 'round-time-missing',
        round: r,
        error: `Round ${r} needs a start time.`,
        roundStartTimes: null,
      };
    }
    if (ms <= prevMs) {
      return {
        ok: false,
        code: 'round-time-order',
        round: r,
        error: `Round ${r} must start after round ${r - 1}.`,
        roundStartTimes: null,
      };
    }
    out[r] = ms;
    prevMs = ms;
  }

  return { ok: true, code: null, round: null, error: null, roundStartTimes: out };
}

export function buildRoundSchedulePayload({
  mode = 'gap',
  interRoundGapMinutes = DEFAULT_GAP_MINUTES,
  roundStartTimes = null,
  totalRounds = 1,
} = {}) {
  const rounds = Math.floor(Number(totalRounds)) || 0;
  if (mode === 'scheduled' && rounds >= 2) {
    const raw = roundStartTimes && typeof roundStartTimes === 'object' ? roundStartTimes : {};
    const out = {};
    for (let r = 2; r <= rounds; r++) {
      const ms = toEpochMs(raw[r] ?? raw[String(r)]);
      out[r] = Number.isFinite(ms) ? ms : null;
    }
    return {
      roundScheduleMode: 'scheduled',
      roundStartTimes: out,
    };
  }
  return {
    roundScheduleMode: 'gap',
    interRoundGapMinutes: Number(interRoundGapMinutes) || DEFAULT_GAP_MINUTES,
  };
}

export function mapRoundScheduleError(errOrMessage) {
  const raw = typeof errOrMessage === 'string'
    ? errOrMessage
    : (errOrMessage?.message || errOrMessage?.details || '');
  const msg = String(raw || '').trim();
  if (!msg) return '';
  if (msg.includes('round-schedule-needs-start')) {
    return 'Pick a start time for the event first.';
  }
  if (msg.includes('round-time-missing')) {
    const m = msg.match(/Round\s+(\d+)/i);
    const r = m ? Number(m[1]) : 2;
    return `Round ${r} needs a start time.`;
  }
  if (msg.includes('round-time-order')) {
    const m = msg.match(/Round\s+(\d+)/i);
    const r = m ? Number(m[1]) : 2;
    return `Round ${r} must start after round ${r - 1}.`;
  }
  if (msg.includes("This event has already started. Its round times can't change now.")) {
    return "This event has already started. Its round times can't change now.";
  }
  return msg.replace(/^[a-z0-9_-]+:\s*/i, '');
}

export default {
  GAP_STEPS,
  DEFAULT_GAP_MINUTES,
  DAY_ANCHOR_MINUTES,
  toEpochMs,
  formatRoundLocalTime,
  formatGapLabel,
  rowsFromGap,
  roundTimesFromGap,
  normalizeRoundStartTimes,
  validateRoundScheduleClient,
  buildRoundSchedulePayload,
  mapRoundScheduleError,
};
